import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Iter v34 — Fix leaflet default marker icon paths under webpack
// (webpack doesn't auto-resolve the default `_getIconUrl`).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) { onPick(e.latlng.lat, e.latlng.lng); },
  });
  return null;
}

function FlyTo({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat && lng && !Number.isNaN(lat) && !Number.isNaN(lng)) {
      map.flyTo([lat, lng], Math.max(map.getZoom(), 16), { duration: 0.6 });
    }
  }, [lat, lng, map]);
  return null;
}

/**
 * Iter v34 — Map picker untuk menetapkan titik kantor secara akurat.
 * Props:
 *   lat / lng (number|string)  — koordinat aktif
 *   radius (number)            — radius dalam meter (untuk preview lingkaran)
 *   onChange(lat, lng)         — dipanggil saat user klik peta atau drag marker
 */
export default function OfficeMapPicker({ lat, lng, radius = 100, onChange }) {
  const latN = parseFloat(lat);
  const lngN = parseFloat(lng);
  const hasCoord = !Number.isNaN(latN) && !Number.isNaN(lngN);
  const center = hasCoord ? [latN, lngN] : [-2.9909, 104.7569]; // default: Palembang area
  const [draftMarker, setDraftMarker] = useState(hasCoord ? center : null);

  useEffect(() => {
    if (hasCoord) setDraftMarker([latN, lngN]);
  }, [latN, lngN, hasCoord]);

  const handlePick = (newLat, newLng) => {
    setDraftMarker([newLat, newLng]);
    onChange && onChange(newLat.toFixed(7), newLng.toFixed(7));
  };

  return (
    <div className="border border-stone-200" data-testid="office-map-picker">
      <div className="bg-stone-50 px-3 py-2 text-[11px] text-stone-600 border-b border-stone-200 flex items-center justify-between">
        <span><b>Klik peta</b> untuk pin lokasi kantor. Lingkaran biru = preview radius absensi.</span>
        {draftMarker && (
          <span className="font-mono text-stone-700">{Number(draftMarker[0]).toFixed(5)}, {Number(draftMarker[1]).toFixed(5)}</span>
        )}
      </div>
      <div style={{ height: 360, width: "100%" }}>
        <MapContainer center={center} zoom={hasCoord ? 17 : 6} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onPick={handlePick} />
          {draftMarker && (
            <>
              <Marker
                position={draftMarker}
                draggable
                eventHandlers={{
                  dragend: (e) => {
                    const m = e.target.getLatLng();
                    handlePick(m.lat, m.lng);
                  },
                }}
              />
              <Circle
                center={draftMarker}
                radius={Number(radius) || 100}
                pathOptions={{ color: "#2563eb", fillColor: "#3b82f6", fillOpacity: 0.12 }}
              />
            </>
          )}
          {hasCoord && <FlyTo lat={latN} lng={lngN} />}
        </MapContainer>
      </div>
    </div>
  );
}
